<template>
  <v-form ref="form">
    <v-row v-for="(sensor, index) in sensors" :key="index" align="center">
      <v-col cols="12" md="2">
        <div class="tertiary--text text-body-2">
          {{
            $t(
              'fleet_management.agents.create.labels.create_agent_form_temperature_multi_sensor_sensor'
            )
          }}
        </div>
        <v-text-field
          v-model.number="sensor.sensor"
          dense
          filled
          type="number"
          :rules="sensorIndexRules"
          @blur="handleSensorBlur(sensor)"
          :readonly="!editMode"
          min="0"
          max="7"
          ref="sensorFields"
          :ref="`sensorField-${index}`"
        />
      </v-col>

      <v-col cols="12" md="3">
        <div class="tertiary--text text-body-2">
          {{
            $t(
              'fleet_management.agents.create.labels.create_agent_form_temperature_sensor_minimum_percentage'
            )
          }}
        </div>
        <v-text-field
          v-model.number="sensor.min_temp"
          dense
          filled
          type="number"
          :error-messages="sensor.minErrorMessage"
          :rules="minTemperatureSensorRules"
          @input="temperatureMultiInput(sensor)"
          @blur="sensor.min_temp = formatTemperature(sensor.min_temp, 0.0)"
          :readonly="!editMode"
          step="0.1"
        />
      </v-col>

      <v-col cols="12" md="3">
        <div class="tertiary--text text-body-2">
          {{
            $t(
              'fleet_management.agents.create.labels.create_agent_form_temperature_sensor_maximum_percentage'
            )
          }}
        </div>
        <v-text-field
          v-model.number="sensor.max_temp"
          dense
          filled
          type="number"
          :error-messages="sensor.maxErrorMessage"
          :rules="maxTemperatureSensorRules"
          @input="temperatureMultiInput(sensor)"
          @blur="sensor.max_temp = formatTemperature(sensor.max_temp, 40.0)"
          :readonly="!editMode"
          step="0.1"
        />
      </v-col>

      <v-col cols="12" md="1" class="d-flex align-center">
        <v-btn
          icon
          @click="removeSensor(index)"
          v-if="editMode && sensors.length > 1"
        >
          <v-icon>
            mdi-delete
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="editMode">
      <v-col cols="12" class="d-flex justify-end mt-2">
        <v-btn
          color="tertiary"
          text
          class="text-capitalize"
          @click="addSensor"
          :disabled="!canAddSensor"
        >
          <v-icon left>mdi-plus-circle-outline</v-icon>
          {{
            $t(
              'fleet_management.agents.create.labels.create_agent_form_temperature_multi_add_sensor'
            )
          }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { i18n } from '@leapsight/portal'

export default {
  name: 'TemperatureMultiSensor',
  props: {
    tempSensorMulti: Object,
    editMode: Boolean
  },
  data() {
    return {
      sensors: [],
      originalSensors: [],
      sensorIndexRules: [
        v =>
          (v !== null && v !== undefined && v !== '') ||
          i18n.t(
            'fleet_management.agents.create.errors.temperature_sensor_empty'
          ),
        v => {
          const count = this.sensors.filter(s => s.sensor === v).length
          return count <= 1 || `El número del sensor ${v} ya existe`
        }
      ],
      minTemperatureSensorRules: [
        v =>
          (v !== null && v !== undefined && v !== '') ||
          i18n.t(
            'fleet_management.agents.create.errors.temperature_sensor_empty'
          ),
        v =>
          v >= -60 ||
          i18n.t(
            'fleet_management.agents.create.errors.temperature_multi_sensor_minimum_range_error'
          )
      ],
      maxTemperatureSensorRules: [
        v =>
          (v !== null && v !== undefined && v !== '') ||
          i18n.t(
            'fleet_management.agents.create.errors.temperature_sensor_empty'
          ),
        v =>
          v <= 85 ||
          i18n.t(
            'fleet_management.agents.create.errors.temperature_multi_sensor_maximum_configured_error'
          )
      ]
    }
  },
  watch: {
    sensors: {
      deep: true,
      handler(newVal) {
        const structuredSensors = {}

        newVal.forEach(sensorObj => {
          if (sensorObj.sensor !== null && sensorObj.sensor !== undefined) {
            const key = `temperature${sensorObj.sensor}`
            structuredSensors[key] = {
              min_temp: this.formatTemperature(sensorObj.min_temp, 0.0),
              max_temp: this.formatTemperature(sensorObj.max_temp, 40.0)
            }
          }
        })

        this.validationsTemperatureMultiSensor()
      }
    }
  },
  computed: {
    canAddSensor() {
      return this.sensors.length < 8
    }
  },
  methods: {
    forceValidateAllSensors() {
      this.sensors.forEach(sensor => {
        this.temperatureMultiInput(sensor)
      })
    },
    addSensor() {
      this.sensors.push({
        sensor: this.getNextSensorNumber(),
        min_temp: 0.0,
        max_temp: 40.0,
        minErrorMessage: '',
        maxErrorMessage: ''
      })
      this.$nextTick(() => {
        this.forceValidateAllSensors()
      })
    },
    removeSensor(index) {
      this.sensors.splice(index, 1)

      this.validationsTemperatureMultiSensor()

      const structuredSensors = {}
      this.sensors.forEach(sensorObj => {
        const key = sensorObj.sensor ? `temperature${sensorObj.sensor}` : null
        if (key) {
          structuredSensors[key] = {
            min_temp: this.formatTemperature(sensorObj.min_temp, 0.0),
            max_temp: this.formatTemperature(sensorObj.max_temp, 40.0)
          }
        }
      })

      this.$emit('tempSensorMulti', structuredSensors)

      this.$nextTick(() => {
        this.$refs.form?.validate()
      })
    },
    temperatureMultiInput(sensor) {
      if (sensor.sensor == null) {
        sensor.sensor = this.getNextSensorNumber()
      }

      sensor.min_temp = this.formatTemperature(sensor.min_temp, 0.0)
      sensor.max_temp = this.formatTemperature(sensor.max_temp, 40.0)

      const minTemp = sensor.min_temp
      const maxTemp = sensor.max_temp

      if (minTemp >= maxTemp) {
        sensor.minErrorMessage = i18n.t(
          'fleet_management.agents.create.errors.temperature_sensor_minimum_lesser_than_maximum_error'
        )
      } else if (minTemp < -60) {
        sensor.minErrorMessage = i18n.t(
          'fleet_management.agents.create.errors.temperature_multi_sensor_minimum_range_error'
        )
      } else if (minTemp > 85) {
        sensor.minErrorMessage = i18n.t(
          'fleet_management.agents.create.errors.temperature_multi_sensor_minimum_configured_error'
        )
      } else {
        sensor.minErrorMessage = ''
      }

      if (maxTemp > 85) {
        sensor.maxErrorMessage = i18n.t(
          'fleet_management.agents.create.errors.temperature_multi_sensor_maximum_configured_error'
        )
      } else {
        sensor.maxErrorMessage = ''
      }
      const structuredSensors = {}

      this.sensors.forEach(sensorObj => {
        if (sensorObj.sensor !== null && sensorObj.sensor !== undefined) {
          const key = `temperature${sensorObj.sensor}`
          structuredSensors[key] = {
            min_temp: this.formatTemperature(sensorObj.min_temp, 0.0),
            max_temp: this.formatTemperature(sensorObj.max_temp, 40.0)
          }
        }
      })

      this.$emit('tempSensorMulti', structuredSensors)
      this.validationsTemperatureMultiSensor()

      if (sensor.min_temp < sensor.max_temp) this.$refs.form.validate()
    },
    validationsTemperatureMultiSensor() {
      const seenSensors = new Set()
      let hasDuplicateSensor = false

      const allSensorsValid =
        this.sensors.length > 0 &&
        this.sensors.every(sensor => {
          const minTemp = this.formatTemperature(sensor.min_temp)
          const maxTemp = this.formatTemperature(sensor.max_temp)

          if (
            sensor.sensor === null ||
            sensor.sensor === undefined ||
            sensor.sensor === '' ||
            minTemp === null ||
            minTemp === undefined ||
            minTemp === '' ||
            maxTemp === null ||
            maxTemp === undefined ||
            maxTemp === '' ||
            minTemp >= maxTemp ||
            minTemp < -60 ||
            minTemp > 85 ||
            maxTemp < -60 ||
            maxTemp > 85
          ) {
            return false
          }

          if (seenSensors.has(sensor.sensor)) {
            hasDuplicateSensor = true
            return false
          }

          seenSensors.add(sensor.sensor)
          return true
        })

      const hasChanges =
        JSON.stringify(this.sensors) !== JSON.stringify(this.originalSensors)

      const finalValid = allSensorsValid && !hasDuplicateSensor

      if (finalValid) this.$emit('temperatureMultiValid', finalValid)

      if (hasChanges && finalValid)
        this.$emit('temperatureMultiChanged', hasChanges && finalValid)
    },
    formatTemperature(value, defaultValue) {
      const number = parseFloat(value)
      return isNaN(number) ? defaultValue : parseFloat(number.toFixed(1))
    },
    getNextSensorNumber() {
      const used = this.sensors
        .map(s => Number(s.sensor))
        .filter(n => !isNaN(n))
      let next = 0
      while (used.includes(next)) {
        next++
      }
      return next
    },
    handleSensorBlur(sensor) {
      if (sensor.sensor == null) {
        const next = this.getNextSensorNumber()
        if (!this.sensors.some(s => s.sensor === next)) {
          sensor.sensor = next
        }
      }
    }
  },
  mounted() {
    if (this.tempSensorMulti && typeof this.tempSensorMulti === 'object') {
      const seen = new Set()
      this.sensors = Object.entries(this.tempSensorMulti)
        .filter(([key]) => /^temperature\d+$/.test(key))
        .map(([key, sensor]) => {
          const sensorNum = parseInt(key.replace('temperature', ''))
          if (seen.has(sensorNum)) return null
          seen.add(sensorNum)

          return {
            sensor: sensorNum,
            min_temp: this.formatTemperature(sensor.min_temp, 0.0),
            max_temp: this.formatTemperature(sensor.max_temp, 40.0),
            minErrorMessage: '',
            maxErrorMessage: ''
          }
        })
        .filter(Boolean)
    }

    this.originalSensors = JSON.parse(JSON.stringify(this.sensors))

    if (!this.sensors.length) {
      this.addSensor()
    }

    this.validationsTemperatureMultiSensor()
  }
}
</script>
